import { User } from "@axl-dev/school-sdk";
import { Avatar } from "antd";
import cn from "classnames";
import { useState, useEffect, useMemo, useCallback } from "react";

import { useFileProvider } from "../lib/hooks";
import { isEmpty } from "../lib/is-empty";

const defaultColors = [
  "#2ecc71", // emerald
  "#3498db", // peter river
  "#8e44ad", // wisteria
  "#e67e22", // carrot
  "#e74c3c", // alizarin
  "#1abc9c", // turquoise
  "#2c3e50", // midnight blue
];

type UserAvatarProps = {
  user: User;
  shape?: "circle" | "square";
  size?: "large" | "small" | "default" | number;
  color?: string;
  useShortName?: boolean;
  className?: string;
  resize?: { w?: number; h?: number };
};

type UserAvatarState = {
  avatarUrl: string | null;
  shortName: string;
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  shape = "circle",
  size = "default",
  useShortName = true,
  resize = { w: 85, h: 85 },
  ...props
}) => {
  const fileProvider = useFileProvider();

  const shortName = useMemo(() => {
    let str = "";
    if (!isEmpty(user.firstName)) str += user.firstName[0]!.toUpperCase();
    if (!isEmpty(user.lastName)) str += user.lastName[0]!.toUpperCase();
    if (isEmpty(str) && !isEmpty(user.email))
      return user.email[0]!.toUpperCase();

    return str;
  }, [user]);

  const getState = (): UserAvatarState => {
    return {
      avatarUrl:
        user.avatar != null ? (fileProvider.getUrl(user.avatar) ?? null) : null,
      shortName: useShortName === false ? user.fullName : shortName,
    };
  };

  const [state, setState] = useState<UserAvatarState>(getState());

  useEffect(() => {
    setState(getState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getRandomColor = useCallback(() => {
    const str = !isEmpty(user.email) ? user.email : user.fullName;
    if (isEmpty(str)) return defaultColors[0];
    let sum = 0;
    for (let i = 0; i < str.length; i++) {
      sum += str.charCodeAt(i);
    }

    const i = sum % defaultColors.length;

    return defaultColors[i];
  }, [user]);

  const getFontSize = useCallback(() => {
    if (typeof size === "number") return size / 2.5;

    return undefined;
  }, [size]);

  const style = useMemo(() => {
    if (state.avatarUrl) return undefined;

    return {
      backgroundColor: props.color ?? getRandomColor(),
      fontSize: getFontSize(),
    } as React.CSSProperties;
  }, [state.avatarUrl, props.color, getRandomColor, getFontSize]);

  const url = useMemo(() => {
    let avatarUrl = state.avatarUrl;
    if (avatarUrl?.startsWith("http") && resize) {
      try {
        const parsedUrl = new URL(avatarUrl);
        if (resize.h) parsedUrl.searchParams.append("d", `h${resize.h}`);
        else if (resize.w) parsedUrl.searchParams.append("d", `w${resize.w}`);
        avatarUrl = parsedUrl.toString();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }

    return avatarUrl;
  }, [resize, state.avatarUrl]);

  return (
    <Avatar
      className={cn(props.className, "flex flex-center")}
      shape={shape}
      size={size}
      style={style}
      src={url ?? undefined}
      onError={() => {
        setState({
          ...state,
          avatarUrl: null,
        });

        return false;
      }}
    >
      {state.shortName}
    </Avatar>
  );
};
